import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Landing page for links meant for the Echo Conversations mobile app.
 *
 * The app claims `/m/*` as a Universal Link / App Link, so on a phone with it
 * installed these URLs open the app directly and this page is never seen.
 *
 * It exists for every other case, and those cases are common: the link was
 * opened on a desktop, on a phone without the app, or pasted into a browser
 * rather than tapped from the email. Before this page existed all of those hit
 * the 404, which — on a sign-in link someone is actively trying to use — reads
 * as "Echo is broken" rather than "open this somewhere else".
 */
@Component({
    selector: 'app-mobile-link',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './mobile-link.component.html',
    styleUrls: ['./mobile-link.component.scss']
})
export class MobileLinkComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    /** True while we give the OS a moment to hand the URL to the app instead. */
    isWaiting = true;

    /** Set when this is a sign-in link, which changes the wording. */
    isSignInLink = false;

    isMobile = false;
    isIos = false;

    ngOnInit(): void {
        const ua = navigator.userAgent || '';
        this.isIos = /iPad|iPhone|iPod/.test(ua);
        this.isMobile = this.isIos || /Android/i.test(ua);

        // Firebase's email sign-in links always carry these.
        const params = this.route.snapshot.queryParamMap;
        this.isSignInLink = params.get('mode') === 'signIn' || params.has('oobCode');

        // On a phone the OS may still be deciding whether to hand this to the
        // app. Showing the fallback instantly would flash "open the app" at
        // someone whose app is about to open anyway, so hold the spinner
        // briefly first.
        const settleMs = this.isMobile ? 1200 : 0;
        setTimeout(() => (this.isWaiting = false), settleMs);
    }

    /**
     * Re-navigate to the same URL to give the OS a second chance to route it
     * into the app. Harmless when the app is not installed — the page simply
     * reloads.
     */
    retryOpenApp(): void {
        window.location.reload();
    }

    goToPortal(): void {
        // A sign-in link is single-use per address, so send people to the
        // normal login rather than pretending the link still works here.
        void this.router.navigate(['/auth/login']);
    }
}
